import useModal from "hooks/useModal";
import React, { useState, useEffect, useMemo } from "react";
import { Button, Modal, Form, Row, Col, Badge } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { auditLogsFilter } from "store/slices/filter";

const AdvancedFilterPopup = () => {
  const { show: showPopup, open: openFiltersPopup, close: closePopup } = useModal();

  const predefinedFilters = [
    { label: "Subject ID", value: "subject_id" },
    { label: "Subject Role", value: "subject_role" },
  ];

  const dispatch = useDispatch();
  const existingFilters = useSelector((state) => state?.filter?.auditLogsFilter);

  const [filters, setFilters] = useState([{ key: "", value: "" }]);

  const allowedKeys = useMemo(() => predefinedFilters.map((f) => f.value), []);

  // Sync relevant filters (those in predefinedFilters)
  useEffect(() => {
    if (!existingFilters) return;

    const relevantEntries = Object.entries(existingFilters).filter(([key]) =>
      allowedKeys.includes(key)
    );

    if (relevantEntries.length > 0) {
      setFilters(relevantEntries.map(([key, value]) => ({ key, value })));
    } else {
      setFilters([{ key: "", value: "" }]);
    }
  }, [existingFilters, allowedKeys]);

  const hasActiveAdvancedFilters = useMemo(() => {
    if (!existingFilters) return false;
    return Object.entries(existingFilters).some(
      ([key, value]) => allowedKeys.includes(key) && value?.trim() !== ""
    );
  }, [existingFilters, allowedKeys]);

  const handleAddRow = () => setFilters((prev) => [...prev, { key: "", value: "" }]);

  const handleRemoveRow = (index) => {
    const updated = filters.filter((_, i) => i !== index);
    setFilters(updated.length ? updated : [{ key: "", value: "" }]);
  };

  const handleChange = (index, field, newValue) => {
    setFilters((prev) => {
      const updated = [...prev];
      updated[index][field] = newValue;
      return updated;
    });
  };

  const handleApply = () => {
    const validFilters = filters.filter((f) => f.key && f.value.trim() !== "");

    // If no valid filters, remove only the predefined ones from redux
    if (validFilters.length === 0) {
      const filtered = Object.fromEntries(
        Object.entries(existingFilters || {}).filter(([key]) => !allowedKeys.includes(key))
      );
      dispatch(auditLogsFilter(filtered));
      closePopup();
      return;
    }

    // Build payload for only the predefined filters
    const payload = Object.fromEntries(validFilters.map((f) => [f.key, f.value]));

    // Merge back into redux while preserving other filters
    const merged = {
      ...(existingFilters || {}),
      ...payload,
    };
    dispatch(auditLogsFilter(merged));
    closePopup();
  };

  return (
    <>
      <Button
        className="btn forteen text-white d-flex align-items-center justify-content-center commonBtn fw-sbold position-relative"
        onClick={openFiltersPopup}
      >
        <i className="fa fa-sliders me-2"></i>Advanced Filters
        {hasActiveAdvancedFilters && (
          <Badge
            bg="danger"
            pill
            className="position-absolute top-0 start-100 translate-middle p-2 border border-light rounded-circle d-block"
            style={{ width: "5px", height: "5px" }}
          ></Badge>
        )}
      </Button>
      <Modal
        show={showPopup}
        onHide={closePopup}
        backdrop="static"
        keyboard={false}
        centered
        className="advanced-filter-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Advanced Filters</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            {filters.map((filter, index) => (
              <Row key={index} className="align-items-end mb-3">
                <Col md={5}>
                  <Form.Group>
                    <Form.Label>Filter</Form.Label>
                    <Form.Select
                      className="form-control"
                      value={filter.key}
                      onChange={(e) => handleChange(index, "key", e.target.value)}
                    >
                      <option value="">Select Filter</option>
                      {predefinedFilters.map((f) => (
                        <option key={f.value} value={f.value}>
                          {f.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={5}>
                  <Form.Group>
                    <Form.Label>Value</Form.Label>
                    <Form.Control
                      type="text"
                      value={filter.value}
                      onChange={(e) => handleChange(index, "value", e.target.value)}
                      placeholder="Enter value"
                    />
                  </Form.Group>
                </Col>

                <Col md={2} className="text-end">
                  {index === filters.length - 1 ? (
                    <Button
                      variant="light"
                      className="fw-sbold border themeClr"
                      onClick={handleAddRow}
                      title="Add Filter"
                    >
                      <i className="fa fa-plus-circle fs-5"></i>
                    </Button>
                  ) : (
                    <Button
                      variant="light"
                      className="fw-sbold border"
                      onClick={() => handleRemoveRow(index)}
                      title="Remove Filter"
                    >
                      <i className="fa fa-minus-circle text-danger fs-5"></i>
                    </Button>
                  )}
                </Col>
              </Row>
            ))}
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="primary" onClick={handleApply} className="fw-sbold commonBtn">
            <i className="fa fa-filter me-2"></i>Apply
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AdvancedFilterPopup;
